import React from 'react';
import { Server, ShieldCheck, Heart, Zap, Activity, ShieldAlert, Cpu } from 'lucide-react';
import { motion } from 'framer-motion';

// 🔥 DİL ENTEGRASYONU 🔥
import { useLanguage } from '../context/LanguageContext';

const TrustBarItem = ({ icon, title, description, isLast, extra }) => {
  return (
    <div className={`flex items-center gap-5 flex-1 ${!isLast ? 'lg:border-r lg:border-white/5' : ''} py-6 lg:py-4 px-8 justify-center lg:justify-start group transition-all duration-500 relative overflow-hidden`}>
      
      {/* Background Hover Effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-white/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />

      {/* Icon Terminal: Neon Border & Glow */}
      <div className="relative">
          <div className="absolute inset-0 bg-blue-500/20 blur-xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 rounded-full"></div>
          <div className="p-3.5 bg-[#0f172a] rounded-2xl border border-white/10 shadow-[0_0_20px_rgba(0,0,0,0.5)] group-hover:border-orange-500/30 group-hover:shadow-[0_0_20px_rgba(234,88,12,0.2)] transition-all duration-500 relative z-10">
            {React.cloneElement(icon, { size: 22, strokeWidth: 2, className: "text-slate-400 group-hover:text-orange-500 transition-colors duration-500" })}
          </div>
      </div>
      
      <div className="space-y-1 relative z-10">
        {/* UPPER TITLE: High-Tech Label */}
        <p className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-500 group-hover:text-blue-400 transition-colors flex items-center gap-2">
          {title}
        </p>
        
        {/* CORE TEXT: Bold & Sharp */}
        <div className="text-sm font-black text-white flex items-center gap-2 tracking-tighter uppercase italic">
          {description}
          {extra}
        </div>
      </div>
    </div>
  );
};

const TrustBar = () => {
  const { t } = useLanguage(); // Dil fonksiyonunu al

  return (
    <section className="w-full bg-[#020617] border-t border-white/5 relative overflow-hidden py-16">
      
      {/* Kinetic Background Light */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-orange-500/30 to-transparent" />
      <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-blue-600/5 blur-[150px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6">
        {/* Main Dock: Elite Glassmorphism */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="flex flex-col lg:flex-row justify-between items-stretch gap-2 lg:gap-0 bg-[#0f172a]/60 rounded-[3rem] border border-white/10 p-2 backdrop-blur-2xl shadow-[0_40px_100px_-20px_rgba(0,0,0,0.8)] ring-1 ring-white/5 relative overflow-hidden"
        >
          {/* Decorative Grid Overlay */}
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-[0.05] pointer-events-none mix-blend-overlay"></div>
          
          {/* 1. Data Center Operation */}
          <TrustBarItem 
            icon={<Server />}
            title={t('trust_datacenter_title') || "INFRASTRUCTURE"}
            description="FRANKFURT REGION"
            extra={
              <div className="flex items-center gap-1.5 ml-2 bg-emerald-500/10 px-2 py-0.5 rounded-md border border-emerald-500/20">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                </span>
                <span className="text-[8px] font-mono font-bold text-emerald-500 tracking-widest uppercase">LIVE</span>
              </div>
            }
          />

          {/* 2. Privacy & Compliance Protocol */}
          <TrustBarItem 
            icon={<ShieldCheck />}
            title={t('trust_privacy_title') || "SECURITY PROTOCOL"}
            description="GDPR / DSGVO VERIFIED"
            extra={<ShieldAlert size={14} className="text-blue-500 animate-pulse ml-1" />}
          />

          {/* 3. Engineering Origin */}
          <TrustBarItem 
            icon={<Cpu />}
            title={t('trust_origin_title') || "ENGINEERING"}
            description="MADE IN GERMANY"
            isLast={true}
            extra={<Heart size={12} className="text-rose-600 fill-rose-600 ml-1" />}
          />

        </motion.div>

        {/* Sub-label: System Intelligence */}
        <div className="mt-10 flex flex-col md:flex-row items-center justify-center gap-6 md:gap-16 opacity-40 group cursor-default">
            <div className="flex items-center gap-3 hover:opacity-100 transition-opacity">
               <ShieldAlert size={14} className="text-slate-400" />
               <span className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-400 italic">Military Grade Encryption (AES-256)</span>
            </div>
            <div className="w-1 h-1 bg-slate-600 rounded-full hidden md:block" />
            <div className="flex items-center gap-3 hover:opacity-100 transition-opacity">
               <Activity size={14} className="text-slate-400" />
               <span className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-400 italic">24/7 Redundant Server Monitoring</span>
            </div>
        </div>
      </div>
    </section>
  );
};

export default TrustBar;